import { CartQueryResult } from '@graphql/queries/__generated__/cart.generated'
import Button from '@mui/material/Button'
import React from 'react'
import Loader from '@components/Loader'
import { Link } from 'react-router-dom'
import { WHOLESALE_CHECKOUT_URL } from '../../../urls'
import FormattedPrice from '@components/FormattedPrice'
import { useTranslation } from 'react-i18next'
import Grid from '@mui/material/Grid'

const Buttons: React.FC<{
  orderId: number | null
  onSaveAsDraft(): Promise<void>
  onAddNote(): void
  onDeleteOrder(): void
}> = ({ orderId, onSaveAsDraft, onAddNote, onDeleteOrder }) => {
  const { t } = useTranslation()
  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Button
            component={Link}
            to={WHOLESALE_CHECKOUT_URL}
            color='secondary'
            variant='contained'
            size='large'
            disableElevation
            fullWidth
          >
            <span className='text-base tracking-1 font-bold'>
              {orderId
                ? t('button.continueToCheckout', 'Continue to Checkout')
                : t('button.checkoutNow', 'Checkout now')}
            </span>
          </Button>
        </Grid>
        {orderId && (
          <>
            <Grid item xs={6} md={12}>
              <Button
                color='primary'
                variant='outlined'
                size='medium'
                onClick={onSaveAsDraft}
                disableElevation
                fullWidth
              >
                <span className='text-base tracking-1 font-bold'>{t('button.saveAsDraft', 'Save as Draft')}</span>
              </Button>
            </Grid>
            <Grid item xs={6} md={12}>
              <Button color='primary' variant='outlined' size='medium' onClick={onAddNote} disableElevation fullWidth>
                <span className='text-base tracking-1 font-bold'>{t('button.addNote', 'Add a note')}</span>
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                color='warning'
                variant='outlined'
                size='medium'
                onClick={onDeleteOrder}
                disableElevation
                fullWidth
              >
                <span className='text-base tracking-1 font-bold'>{t('button.deleteOrder', 'Delete order')}</span>
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}

interface CartLinesTotalProps {
  loading: boolean
  cartQueryResult: CartQueryResult
  onSaveAsDraft(): Promise<void>
  onAddNote(): void
  onDeleteOrder(): void
}

const CartLinesTotal: React.FC<CartLinesTotalProps> = ({
  cartQueryResult,
  loading,
  onSaveAsDraft,
  onAddNote,
  onDeleteOrder,
}) => {
  const { t } = useTranslation()
  const itemSubtotal = cartQueryResult.data?.cart?.totalPrice.discount.gross
  const currency = cartQueryResult.data?.cart?.totalPrice.discount.currency || 'USD'
  if (!cartQueryResult.data?.cart?.count) return null

  return (
    <>
      <div className='bg-white shadow-md md:sticky z-5' style={{ top: '120px' }}>
        <ul className='list-none p-12 text-base mb-0'>
          <li className='py-5 flex items-center justify-between'>
            <div>
              <div>{t('shipping', 'Shipping')}</div>
              <div className='text-sm text-gray'>
                {t('shippingCalculatedInCheckout', 'Shipping calculated in Checkout')}
              </div>
            </div>
          </li>
          <li className='py-5 font-bold'>
            <div className='flex items-center justify-between'>
              <div>{t('estimatedTotal', 'Estimated Total')}</div>
              <div className='relative'>
                <Loader
                  className='spinner spinner--xs'
                  type='ThreeDots'
                  color='#333'
                  visible={loading}
                  height={10}
                  width={20}
                />
                <span className={loading ? 'opacity-0' : ''}>
                  <FormattedPrice currency={currency} amount={itemSubtotal} />
                </span>
              </div>
            </div>
            <div className='text-sm text-gray font-normal'>
              {t('taxCalculatedInCheckout', 'Tax calculated in Checkout')}
            </div>
          </li>
        </ul>
        <div className='p-12 hidden md:block'>
          <Buttons
            orderId={cartQueryResult.data.cart.orderId}
            onSaveAsDraft={onSaveAsDraft}
            onAddNote={onAddNote}
            onDeleteOrder={onDeleteOrder}
          />
        </div>
      </div>
      <div
        className='fixed block md:hidden bottom-0 bg-white z-10 w-full p-12 left-0 right-0'
        style={{
          boxShadow: '0 -2px 4px 0 rgb(0 0 0 / 10%), 0 0 20px 0 rgb(0 0 0 / 10%)',
        }}
      >
        <div className='font-bold text-center text-base'>
          <span>{t('estimatedTotal', 'Estimated Total')}: </span>{' '}
          <span className='relative'>
            <Loader
              className='spinner spinner--xs'
              type='ThreeDots'
              color='#333'
              visible={loading}
              height={100}
              width={100}
            />
            <span className={loading ? 'opacity-0' : ''}>
              <FormattedPrice currency={currency} amount={itemSubtotal} />
            </span>
          </span>
        </div>
        <div className='pt-12'>
          <Buttons
            orderId={cartQueryResult.data.cart.orderId}
            onSaveAsDraft={onSaveAsDraft}
            onAddNote={onAddNote}
            onDeleteOrder={onDeleteOrder}
          />
        </div>
      </div>
    </>
  )
}

export default CartLinesTotal
